import {
  supabase,
  Sentry,
  isDev,
  SupabaseError,
  withRetry,
  shouldRethrow,
  describeSupabaseFailure,
} from './shared'
import { shouldReportTransientOnce } from './expected-failure'
import type { Sound, SoundCollection } from '../supabase-types'
import { soundsApi, LIST_COLUMNS } from './sounds'
import { collectionCoverSlug, hasCollectionCover } from '../collection-image'

/**
 * Membership rows point at sounds regardless of `is_active`, so every read of
 * `collection_items` has to restrict the embed itself — the filter belongs
 * here, next to the join, not at the render site (TES-2004).
 *
 * `!inner` is load-bearing and not a style choice. PostgREST applies
 * `sounds.is_active=eq.true` to the *embed* only: without `!inner` the parent
 * row survives with `sounds: null`, so `count: 'exact'` still returns every
 * membership row while the grid's `flatMap` drops the null ones. Verified
 * against prod on SFX Toolkit: 51 rows either way without it, 43 with it,
 * against 43 genuinely active members. That gap is a count that disagrees with
 * the list it annotates.
 *
 * The embed needs at least one column to be a valid select target; `id` is the
 * cheapest and the row is discarded anyway.
 */
const ACTIVE_JOIN_COLUMN = 'id'

/**
 * Collections already reported this process, so an ISR revalidation every 60s
 * doesn't republish the same alarm forever. Keyed by `${id}:${slug}` so a
 * subsequent rename (new derived slug) re-alarms.
 */
const reportedInvisibleCollections = new Set<string>()

/** Test-only: reset the Sentry de-dupe ledger between cases. */
export function __resetInvisibleCollectionReports(): void {
  reportedInvisibleCollections.clear()
}

/**
 * Drop collections with no cover image on disk — a coverless collection would
 * render as a broken/placeholder card. Filtering here removes them from every
 * listing (home marquee, /sounds grid, /sounds/collection index) and makes
 * direct /sounds/collection/<id> URLs 404, since the [slug] page resolves
 * against this same list.
 *
 * A collection with `is_public = true` is one somebody intended to ship, so
 * dropping it is a content bug, not routine filtering: report it loudly
 * (TES-1604). Silence here hid "Wild Kingdom" -> "Nature's Soundtrack" for
 * ~2 months. Private/draft collections are filtered quietly, as before.
 */
function keepCoveredCollections(
  rows: SoundCollection[],
  action: string
): SoundCollection[] {
  return rows.filter((c) => {
    if (hasCollectionCover(c)) return true
    if (!c.is_public) return false

    const slug = collectionCoverSlug(c)
    const key = `${c.id}:${slug}`
    if (!reportedInvisibleCollections.has(key)) {
      reportedInvisibleCollections.add(key)
      Sentry.captureException(
        new Error(
          `Public collection "${c.name}" (${c.id}) has no cover image at ` +
            `/images/collections/${slug}.webp and is invisible sitewide. ` +
            `Add the cover and run scripts/gen-collection-covers.mjs, or set is_public = false.`
        ),
        {
          tags: { context: 'collectionsApi', action, issue: 'invisible-public-collection' },
          extra: { collectionId: c.id, collectionName: c.name, coverSlug: slug },
        }
      )
    }
    return false
  })
}

// Sound collections API
export const collectionsApi = {
  // Get all collections.
  //
  // Two distinct failure modes, handled differently on purpose (TES-864):
  //
  //  1. No public Supabase env  → `supabase` is null (lib/supabase-client.ts
  //     intentionally returns null "for CI/build safety"). This is the
  //     CI `next build` prerender of /sounds, where NEXT_PUBLIC_SUPABASE_*
  //     are absent. Hard-throwing here rejected the page's Promise.all and
  //     failed the whole export. Degrade to an empty list instead: the
  //     static shell builds, and ISR (revalidate=60) repopulates from the
  //     real DB on the first runtime request (prod always has the env).
  //
  //  2. Client present but the query fails → still throw, so ISR
  //     revalidation rejects and Next.js keeps the last good cached render
  //     rather than caching an empty list. This is the behavior the
  //     silent-return-[] ISR guidance requires; it is unchanged.
  async getCollections(options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<SoundCollection[]> {
    if (!supabase) {
      return []
    }
    try {
      return await withRetry(async () => {
        const { data, error } = await supabase
          .from('sound_collections')
          .select('*')
          .order('name')

        if (error) {
          throw new SupabaseError(`Failed to fetch collections: ${error.message}`, error)
        }

        return keepCoveredCollections((data as SoundCollection[]) || [], 'getCollections')
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'collectionsApi', action: 'getCollections' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  // Get featured collections - since featured column doesn't exist, return all public collections
  async getFeaturedCollections(options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<SoundCollection[]> {
    if (!supabase) {
      return []
    }
    try {
      return await withRetry(async () => {
        const { data, error } = await supabase
          .from('sound_collections')
          .select('*')
          .eq('is_public', true) // Use is_public instead of non-existent featured column
          .order('name') // Sort by name
          .limit(6) // Limit to 6 featured collections

        if (error) {
          throw new SupabaseError(`Failed to fetch featured collections: ${error.message}`, error)
        }

        // Same cover-image guard as getCollections — never surface a coverless collection.
        return keepCoveredCollections((data as SoundCollection[]) || [], 'getFeaturedCollections')
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'collectionsApi', action: 'getFeaturedCollections' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  /**
   * Real sound counts for a set of display-collection ids, keyed by id.
   *
   * A collection id is either a curated slug (search-term backed, counted
   * against `sounds`) or a DB collection UUID (membership rows in
   * `collection_items`), so this resolves each id by kind rather than
   * assuming one table. The curated ids go out as ONE `count_curated_collections`
   * call — thirteen parallel `count=exact` scans is what hit the statement
   * timeout (TES-2142). Each DB collection asks for zero rows via `limit(0)`
   * rather than `head: true`, so a PostgREST error still carries its body —
   * see `describeSupabaseFailure` (TES-2116).
   *
   * An id whose count could not be established is OMITTED from the result
   * rather than defaulted to 0. The caller then renders no count for it: a
   * "0 sounds" that means "the query failed" is a false statement about the
   * catalogue, and `curatedCollections[].estimatedCount` is an estimate that
   * must not reach a user-facing surface (docs/ANTI_SLOP.md).
   *
   * Reports at most ONE Sentry event per call, not one per failed id. The
   * failure mode here is a shared one — the DB is slow, or the batch of
   * parallel counts saturated something — so thirteen ids failing together is
   * one defect, and billing it thirteen times is what made this the single
   * largest consumer of the org's error quota (TES-2116).
   *
   * Never throws: this is decoration on a listing, and a failed count must
   * not take down the panel it annotates.
   */
  async getDisplayCollectionCounts(ids: string[]): Promise<Record<string, number>> {
    if (ids.length === 0) return {}

    const { getCuratedCollection } = await import('../curated-collections')

    const curatedIds: string[] = []
    const dbIds: string[] = []
    for (const id of ids) {
      ;(getCuratedCollection(id) ? curatedIds : dbIds).push(id)
    }

    const settled = await Promise.allSettled([
      // BrowseSection resolves only the DB-backed ids (the page already holds
      // the curated ones, cached), so skip the RPC entirely when it has nothing
      // to answer for.
      ...(curatedIds.length > 0 ? [soundsApi.getCuratedCollectionCounts(curatedIds)] : []),
      ...dbIds.map(async (id): Promise<Record<string, number>> => {
        if (!supabase) return {}
        const { count, error, status, statusText } = await supabase
          .from('collection_items')
          .select(`sound_id, sounds!inner(${ACTIVE_JOIN_COLUMN})`, { count: 'exact' })
          .eq('collection_id', id)
          .eq('sounds.is_active', true)
          .limit(0)

        if (error) {
          throw new SupabaseError(
            `Failed to count collection ${id}: ${describeSupabaseFailure(error, status, statusText)}`,
            error,
          )
        }
        return count === null || count === undefined ? {} : { [id]: count }
      }),
    ])

    const counts: Record<string, number> = {}
    const failures: unknown[] = []
    for (const result of settled) {
      if (result.status === 'fulfilled') {
        Object.assign(counts, result.value)
      } else {
        failures.push(result.reason)
      }
    }

    if (failures.length > 0 && shouldReportTransientOnce('getDisplayCollectionCounts', failures[0])) {
      Sentry.captureException(failures[0], {
        tags: { context: 'collectionsApi', action: 'getDisplayCollectionCounts' },
        // Counted in ids, not in settled promises: one rejected curated batch
        // is thirteen countless rows, and that is the number worth knowing.
        extra: { requested: ids.length, failed: ids.length - Object.keys(counts).length },
      })
    }

    return counts
  },

  // Get sounds in a collection
  async getCollectionSounds(
    collectionId: string,
    options: { throwOnError?: boolean } = {}
  ): Promise<Sound[]> {
    // Build-time (no Supabase env): degrade to empty instead of throwing.
    if (!supabase) return []
    try {
      return await withRetry(async () => {
        const { data, error } = await supabase!
          .from('collection_items')
          // Embed LIST_COLUMNS (not '*') so the heavy columns (embedding,
          // search_vector, transcript) stay out of the rows serialized into the
          // client CollectionSortGrid (TES-834).
          .select(`
            sound_id,
            sounds!inner (${LIST_COLUMNS})
          `)
          .eq('collection_id', collectionId)
          .eq('sounds.is_active', true)
          .order('order_index') // This column exists in collection_items table

        if (error) {
          throw new SupabaseError(`Failed to fetch collection sounds: ${error.message}`, error)
        }

        return (data?.flatMap((item: any) => item.sounds ? [item.sounds] : []) as Sound[]) || []
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'collectionsApi', action: 'getCollectionSounds' } })
      // ISR callers pass throwOnError so a transient failure rejects the render
      // and Next keeps the last good snapshot instead of caching an empty grid.
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  async getPreviewSounds(collectionId: string, collectionName?: string): Promise<Sound[]> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }
        
        if (isDev) console.log(`[Supabase] Fetching preview sounds for collection ${collectionId}`);
        const { data, error } = await supabase
          .from('collection_items')
          .select('sounds!inner(*)')
          .eq('collection_id', collectionId)
          .eq('sounds.is_active', true)
          .limit(4);

        if (error) {
          throw new SupabaseError(`Failed to fetch preview sounds: ${error.message}`, error);
        }

        const sounds = data?.flatMap((item: any) => item.sounds ? [item.sounds] : []) as Sound[];
        if (isDev) console.log(`[Supabase] Found ${sounds.length} preview sounds`);

        // If we got sounds from the junction table, return them
        if (sounds.length > 0) {
          return sounds;
        }

        // Otherwise, fall back to category-based fetching based on collection name
        if (collectionName) {
          if (isDev) console.log(`[Supabase] Using category fallback for: ${collectionName}`);
          return this.getPreviewSoundsByCollectionName(collectionName);
        }

        return sounds;
      });
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'collectionsApi', action: 'getPreviewSounds' } })
      // Try fallback if we have a collection name
      if (collectionName) {
        return this.getPreviewSoundsByCollectionName(collectionName);
      }
      return [];
    }
  },

  // Helper method to get preview sounds based on collection name (fallback when collection_items is empty)
  async getPreviewSoundsByCollectionName(collectionName: string, options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<Sound[]> {
    // Build-time safety: no client means the static shell prerender; degrade to []
    // and let ISR repopulate on the first runtime request.
    if (!supabase) {
      return []
    }
    const { throwOnError } = options
    try {
      const nameLower = collectionName.toLowerCase();

      // Popular/trending collections
      if (nameLower.includes('popular') || nameLower.includes('favorites') || nameLower.includes('viral') || nameLower.includes('trending')) {
        return await soundsApi.getSounds({ sortBy: 'downloads', sortOrder: 'desc', limit: 4, throwOnError });
      }
      // New releases
      if (nameLower.includes('new') || nameLower.includes('release') || nameLower.includes('latest')) {
        return await soundsApi.getSounds({ sortBy: 'created_at', sortOrder: 'desc', limit: 4, throwOnError });
      }
      // Gaming collections (use 'games' - the lowercase category from seed data)
      if (nameLower.includes('gaming') || nameLower.includes('game')) {
        return await soundsApi.getSounds({ category: 'games', limit: 4, throwOnError });
      }
      // Sci-Fi collections
      if (nameLower.includes('sci-fi') || nameLower.includes('scifi') || nameLower.includes('space') || nameLower.includes('futur')) {
        return await soundsApi.getSounds({ category: 'Sci-Fi', limit: 4, throwOnError });
      }
      // Tech/Nostalgia collections
      if (nameLower.includes('tech') || nameLower.includes('nostalgia') || nameLower.includes('retro') || nameLower.includes('classic')) {
        return await soundsApi.getSounds({ category: 'Tech Nostalgia', limit: 4, throwOnError });
      }
      // Futuristic collections
      if (nameLower.includes('cyber') || nameLower.includes('punk')) {
        return await soundsApi.getSounds({ category: 'Cyberpunk', limit: 4, throwOnError });
      }
      // Comedy collections
      if (nameLower.includes('comedy') || nameLower.includes('funny') || nameLower.includes('humor')) {
        return await soundsApi.getSounds({ category: 'Comedy', limit: 4, throwOnError });
      }

      // Default fallback: return popular sounds
      return await soundsApi.getSounds({ sortBy: 'downloads', sortOrder: 'desc', limit: 4, throwOnError });
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'collectionsApi', action: 'getPreviewSoundsByCollectionName' } });
      if (shouldRethrow(options.throwOnError)) throw error
      return [];
    }
  }
}
